export const formatNumber = (number, locale = 'de-DE') => {
  if (typeof number !== 'number' && typeof number !== 'string') return '';
  return new Intl.NumberFormat(locale).format(number);
};

const USD_FORMATTER = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

// Single money formatter for the whole product: $1,234.50
export const formatMoney = (amount) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (typeof num !== 'number' || Number.isNaN(num)) return USD_FORMATTER.format(0);
  return USD_FORMATTER.format(num);
};

export const formatCurrencyUS = formatMoney;

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const toValidDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const parseEuroNumber = (formattedNumber) => {
  if (!formattedNumber) {
    return 0;
  }
  return parseFloat(String(formattedNumber).replace(/\./g, '').replace(/,/g, '.'));
};

// Single date formatter for the whole product: Oct 9, 2026
export const formatDate = (date, empty = '-') => {
  const valid = toValidDate(date);
  return valid ? DATE_FORMATTER.format(valid) : empty;
};

const pad = (value) => String(value).padStart(2, '0');

// Oct 9, 2026, 14:05:03 UTC
export const formatDateWithTimezone = (date) => {
  const valid = toValidDate(date);
  if (!valid) return '-';
  const day = new Date(Date.UTC(valid.getUTCFullYear(), valid.getUTCMonth(), valid.getUTCDate(), 12));
  const time = `${pad(valid.getUTCHours())}:${pad(valid.getUTCMinutes())}:${pad(valid.getUTCSeconds())}`;
  return `${DATE_FORMATTER.format(day)}, ${time} UTC`;
};

export const formatValidNumber = (value) => {
  return typeof value === 'number' ? value : 'N/A';
};

/**
 * Normalizes IBAN by trimming, removing spaces and converting to uppercase
 * @param {string} iban - The IBAN string to normalize
 * @returns {string} - Normalized IBAN (uppercase, no spaces)
 */
export const normalizeIBAN = (iban) => {
  if (!iban || typeof iban !== 'string') return '';
  return iban.trim().replace(/\s+/g, '').toUpperCase();
};

/**
 * Normalizes bank account number by trimming, removing spaces and hyphens, converting to uppercase
 * @param {string} accountNumber - The bank account number to normalize
 * @returns {string} - Normalized account number (uppercase, no spaces/hyphens)
 */
export const normalizeBankAccountNumber = (accountNumber) => {
  if (!accountNumber || typeof accountNumber !== 'string') return '';
  return accountNumber
    .trim()
    .replace(/[\s-]+/g, '')
    .toUpperCase();
};

/**
 * MOD-97-10 checksum for IBAN (ISO 13616).
 * Rearranges (move first 4 chars to end), converts A=10..Z=35, then remainder mod 97 must be 1.
 * Uses digit-by-digit modulo to support IBAN length without BigInt.
 * @param {string} normalizedIban - Uppercase, no spaces
 * @returns {boolean}
 */
const isValidIBANChecksum = (normalizedIban) => {
  const rearranged = normalizedIban.slice(4) + normalizedIban.slice(0, 4);
  let numeric = '';
  for (const c of rearranged) {
    if (c >= 'A' && c <= 'Z') {
      numeric += String(c.charCodeAt(0) - 55);
    } else {
      numeric += c;
    }
  }
  let remainder = 0;
  for (let i = 0; i < numeric.length; i++) {
    remainder = (remainder * 10 + parseInt(numeric[i], 10)) % 97;
  }
  return remainder === 1;
};

/**
 * Validates standard IBAN format (ISO 13616).
 * 1. Trims input, removes spaces, converts to uppercase
 * 2. Structure: 2 letters (country) + 2 digits (check) + 11-30 alphanumeric (BBAN)
 * 3. Total length: 15-34 characters
 * 4. MOD-97-10 checksum
 * @param {string} iban - The IBAN string to validate
 * @returns {boolean} - True if format and checksum are valid
 */
export const validateIBANFormat = (iban) => {
  if (!iban || typeof iban !== 'string') return false;
  const normalized = normalizeIBAN(iban);
  if (!normalized) return false;
  const ibanRegex = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
  if (!ibanRegex.test(normalized) || normalized.length < 15 || normalized.length > 34) {
    return false;
  }
  return isValidIBANChecksum(normalized);
};

/**
 * Validates non-IBAN bank account number format (local / US / Canada).
 * 1. Trims input
 * 2. Format: at least one digit, 6-64 chars, alphanumeric with optional spaces/hyphens in the middle
 * 3. Accepts formats like US routing/account, Canadian transit/account, etc.
 * @param {string} accountNumber - The bank account number to validate
 * @returns {boolean} - True if format is valid
 */
export const validateBankAccountNumberFormat = (accountNumber) => {
  if (!accountNumber || typeof accountNumber !== 'string') return false;
  const trimmed = accountNumber.trim();
  if (!trimmed) return false;
  // At least one digit, alphanumeric with optional spaces/hyphens, 6-64 chars
  const bankAccountRegex = /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9 -]{4,62}[A-Za-z0-9]$/;
  return bankAccountRegex.test(trimmed);
};

/**
 * Detects if the value looks like standard IBAN structure (2 letters + 2 digits + BBAN, 15-34 chars).
 * @param {string} value - The input to check
 * @returns {boolean}
 */
const looksLikeStandardIBAN = (value) => {
  if (!value || typeof value !== 'string') return false;
  const normalized = normalizeIBAN(value);
  if (!normalized || normalized.length < 15 || normalized.length > 34) return false;
  const ibanStructureRegex = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
  return ibanStructureRegex.test(normalized);
};

/**
 * Unified validator for IBAN / Bank Account Number field.
 * Detects format and runs the appropriate validator:
 * - Standard IBAN (2 letters + 2 digits + BBAN, 15-34 chars) → validateIBANFormat (includes checksum)
 * - Non-IBAN (local / US / Canada) → validateBankAccountNumberFormat
 * @param {string} value - The IBAN or bank account number to validate
 * @returns {boolean} - True if valid
 */
export const validateIBANOrBankAccount = (value) => {
  if (!value || typeof value !== 'string') return false;
  if (looksLikeStandardIBAN(value)) {
    return validateIBANFormat(value);
  }
  return validateBankAccountNumberFormat(value);
};

/**
 * Format IBAN/bank value for sending to server:
 * - Standard IBAN → uppercase, no spaces (compact)
 * - Non-IBAN → trimmed only
 * @param {string} value - The IBAN or bank account number
 * @returns {string}
 */
export const formatIBANForServer = (value) => {
  if (!value || typeof value !== 'string') return '';
  if (looksLikeStandardIBAN(value)) {
    return normalizeIBAN(value);
  }
  return value.trim();
};

/**
 * Format IBAN/bank value for display (e.g. after load/save):
 * - Standard IBAN → uppercase, grouped in blocks of 4 (e.g. "GB29 NWBK 6016 1331 9268 19")
 * - Non-IBAN → trimmed only
 * @param {string} value - The IBAN or bank account number (may be compact from server)
 * @returns {string}
 */
export const formatIBANForDisplay = (value) => {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const normalized = normalizeIBAN(trimmed);
  if (looksLikeStandardIBAN(trimmed)) {
    return normalized.replace(/(.{4})/g, '$1 ').trim();
  }
  return trimmed;
};
